import React, { useState } from "react";
import { Modal, Form, Button, Alert, Spinner } from "react-bootstrap";
import { getErrorDetail } from "../utility/utils.js";
import { fetchOrchidAPI } from "utils/api";
import ResponsiveModal from "components/responsive-modal";
import "./reset-password/reset-password-modal.scss";
import { ReactComponent as CrossClose } from "assets/svg/close_cross_icon.svg";
import PasswordInputContainer from "./reset-password/PasswordInputContainer";
import messages from "messages";

const defaultCriteria = {
  hasMinLength: false,
  hasMixedCaseAndNumber: false,
  hasSpecialChar: false,
};

const defaultErr = {
  currentPasswordErr: "",
  newPasswordErr: "",
};

const PasswordSettingModal = ({ show, onHide }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordCriteria, setPasswordCriteria] = useState(defaultCriteria);
  const [showLoader, setShowLoader] = useState(false);
  const [changeSuccess, setChangeSuccess] = useState("");
  const [err, setErr] = useState(defaultErr);

  const [error, setError] = useState<any>();

  const handleFormReset = () => {
    setNewPassword("");
    setCurrentPassword("");
    setPasswordCriteria(defaultCriteria);
    setErr(defaultErr);
    setChangeSuccess("");
    setError("");
  };

  const checkNewPassword = (password) => {
    const hasMinLength = password.length >= 10;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasMixedCaseAndNumber = hasUpperCase && hasLowerCase && hasNumber;
    const hasSpecialChar = /[\^=+$\-*.[\]{}()?"!@#%&\\,><':;|_~`]/.test(
      password,
    );
    const isDifferentFromCurrent = password !== currentPassword;
    if (password && !isDifferentFromCurrent) {
      setErr((prev) => {
        return {
          ...prev,
          newPasswordErr: messages.samePasswordErr,
        };
      });
    } else if (
      password &&
      isDifferentFromCurrent &&
      err.newPasswordErr === messages.samePasswordErr
    ) {
      setErr((prev) => {
        return {
          ...prev,
          newPasswordErr: "",
        };
      });
    }

    setPasswordCriteria({
      hasMinLength,
      hasMixedCaseAndNumber,
      hasSpecialChar,
    });
  };

  const handlePasswordReset = async () => {
    const response = await fetchOrchidAPI("/api/login/v1/change_password", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        previous_password: currentPassword,
        proposed_password: newPassword,
      }),
    });
    return response;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setShowLoader(true);
    try {
      const response = await handlePasswordReset();
      if (response.ok) {
        setChangeSuccess("Password changed Successfully!");
        setTimeout(() => {
          handleFormReset();
          onHide();
        }, 600);
      } else {
        setError(getErrorDetail(await response.text()));
        if (response.status === 401) {
          setErr((prev) => {
            return {
              ...prev,
              currentPasswordErr: "Invalid credential.",
            };
          });
        }
        return;
      }
    } catch (err) {
      console.error(err);
    } finally {
      setShowLoader(false);
    }
  };

  const checkCurrentPassword = (password) => {
    if (!password) return;
    if (password === newPassword) {
      setErr((prev) => {
        return {
          ...prev,
          newPasswordErr: messages.samePasswordErr,
        };
      });
    } else {
      if (err.newPasswordErr === messages.samePasswordErr) {
        setErr((prev) => {
          return {
            ...prev,
            newPasswordErr: "",
          };
        });
      }
    }
  };

  return (
    <ResponsiveModal centered show={show} onHide={onHide}>
      <Modal.Header className="resetPasswordModalHeader">
        <Modal.Title className="changePasswordHeading">
          Change password
        </Modal.Title>
        <div
          onClick={() => {
            handleFormReset();
            onHide();
          }}
        >
          <CrossClose />
        </div>
      </Modal.Header>
      {error && (
        <Alert variant="danger" className="rounded-0 m-0">
          {error.toString()}
        </Alert>
      )}
      {changeSuccess && (
        <Alert variant="success" className="rounded-0 m-0">
          {changeSuccess}
        </Alert>
      )}
      <Modal.Body className="px-4">
        <Form>
          <PasswordInputContainer
            newPassword={newPassword}
            setNewPassword={setNewPassword}
            currentPassword={currentPassword}
            setCurrentPassword={setCurrentPassword}
            checkCurrentPassword={checkCurrentPassword}
            err={err}
            checkNewPassword={checkNewPassword}
            passwordCriteria={passwordCriteria}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer className="footer">
        <Button
          variant="outline-secondary"
          className="cancelBtn"
          onClick={() => {
            handleFormReset();
            onHide();
          }}
        >
          Cancel
        </Button>
        {showLoader ? (
          <Button variant="primary" disabled type="submit" className="w-25">
            <Spinner
              as="span"
              animation="grow"
              size="sm"
              role="status"
              aria-hidden="true"
            />
          </Button>
        ) : (
          <Button
            disabled={
              !passwordCriteria.hasMinLength ||
              !passwordCriteria.hasMixedCaseAndNumber ||
              !passwordCriteria.hasSpecialChar ||
              currentPassword.length === 0 ||
              currentPassword === newPassword
            }
            type="submit"
            onClick={handleSubmit}
          >
            Update Password
          </Button>
        )}
      </Modal.Footer>
    </ResponsiveModal>
  );
};

export default PasswordSettingModal;
